import React from 'react';
import { View } from 'react-native';
import { Image, type ImageProps } from 'expo-image';
import {
  ANDROID_CSS_BLUR_CLIP_STYLE,
  getCssBlurOverscanStyle,
  getExpoImageBlurProps,
  needsCssBlurOverscanClip,
  type IExpoImageBlurConfig,
} from '@/utils/expoImageBlur';

export type { IExpoImageBlurConfig };

export interface IBlurredExpoImageProps extends ImageProps {
  /**
   * Per-platform blur strengths. When omitted, no blur is applied.
   * For locked thumbnails use {@link LOCKED_CONTENT_BLUR} from `@/constants/lockedContentBlur`.
   */
  blur?: IExpoImageBlurConfig | null;
}

/**
 * Platform-safe `expo-image` wrapper that applies blur without Android RenderScript crashes.
 *
 * ## Production issue: Sentry GROKIO-APP-9C
 *
 * Native Android fatals (`SIGSEGV`, `SEGV_MAPERR`) in RenderScript while blurring images.
 * Typical tombstone frames:
 * - `rsdScriptSetGlobalVar` / `rsdScriptSetGlobalObj`
 * - `android::renderscript::Context::threadProc`
 *
 * `expo-image` `blurRadius` on Android uses Glide → RenderScript (`wasabeef/glide-transformations#196).
 * This component never passes `blurRadius` on Android; API 31+ uses `style.filter: blur()` instead.
 *
 * ## Platform behavior
 *
 * | Platform | Mechanism |
 * |----------|-----------|
 * | iOS | `blurRadius` from `blur.iosBlurRadius` |
 * | Web | `blurRadius` from `blur.webBlurRadius` |
 * | Android 12+ | CSS `filter` from `blur.androidCssBlurPx` (clipped wrapper) |
 * | Android &lt; 12 | No blur (caller overlay only) |
 *
 * ### CSS blur clipping & edge overscan
 *
 * `filter: blur()` (Android 12+) and web `blurRadius` (mapped to CSS `filter: blur()` by
 * expo-image's DOM renderer) share two artifacts that iOS's bitmap blur does not have:
 *
 * 1. The blur can extend past `borderRadius`, so the layer is wrapped in an `overflow: 'hidden'`
 *    parent (`ANDROID_CSS_BLUR_CLIP_STYLE`).
 * 2. The Gaussian kernel samples pixels outside the source bounds; those pixels are transparent
 *    (`TileMode.DECAL` on Android, transparent black on web), so a fringe of half-transparent
 *    pixels appears along each edge of the visible image. The inner image is rendered slightly
 *    larger than its parent via `getCssBlurOverscanStyle()` so the fringe falls outside the
 *    clipped area.
 *
 * References: RN 0.76 `filter` prop, react-native#44453, expo/expo#24572, expo/expo#37837.
 *
 * ## Usage
 *
 * ```tsx
 * import { LOCKED_CONTENT_BLUR } from '@/constants/lockedContentBlur';
 *
 * <BlurredExpoImage
 *   blur={photo.IsLocked ? LOCKED_CONTENT_BLUR : undefined}
 *   source={{ uri }}
 *   className="..."
 * />
 *
 * <BlurredExpoImage
 *   blur={{ iosBlurRadius: 40, webBlurRadius: 12, androidCssBlurPx: 12 }}
 *   source={{ uri }}
 * />
 * ```
 *
 * Profile scroll blur: `ProfileScreen` (inline `PROFILE_SCROLL_BLUR_CONFIG`) + `useProfileAnimations`.
 * Image viewer backdrop: `ImageViewerBackground`.
 *
 * @see utils/expoImageBlur.ts
 * @see constants/lockedContentBlur.ts
 */
export const BlurredExpoImage = React.memo<IBlurredExpoImageProps>(({
  blur,
  style,
  className,
  ...props
}) => {
  const blurProps = getExpoImageBlurProps(blur);

  if (blur && needsCssBlurOverscanClip(blur)) {
    const overscanStyle = getCssBlurOverscanStyle(blur);
    return (
      <View className={className} style={[ANDROID_CSS_BLUR_CLIP_STYLE, style]}>
        <Image
          {...props}
          blurRadius={blurProps.blurRadius}
          style={[overscanStyle, blurProps.style] as ImageProps['style']}
        />
      </View>
    );
  }

  return (
    <Image
      {...props}
      className={className}
      blurRadius={blurProps.blurRadius}
      style={[blurProps.style as ImageProps['style'], style]}
    />
  );
});

BlurredExpoImage.displayName = 'BlurredExpoImage';
